import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import KpiCard from "@/components/KpiCard";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Send, CheckCheck, Eye, AlertCircle, MessageCircle, Pencil, Loader2 } from "lucide-react";

export default function BroadcastDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/broadcasts/${id}`).then(({ data }) => setData(data));
  }, [id]);

  if (!data) return <div className="p-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  const { broadcast: b, logs } = data;
  const s = b.stats || {};

  return (
    <div className="p-6 md:p-8 max-w-[1500px] mx-auto" data-testid="broadcast-detail-page">
      <Link to="/broadcasts" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900 mb-4">
        <ArrowLeft className="w-4 h-4 mr-1" />Back to broadcasts
      </Link>
      <PageHeader
        title={b.name}
        description={`Status: ${b.status}${b.sent_at ? " · sent " + new Date(b.sent_at).toLocaleString() : ""}`}
        actions={
          b.status === "draft" || b.status === "scheduled" ? (
            <Button asChild variant="outline" data-testid="broadcast-edit-button">
              <Link to={`/broadcasts/${b.id}/edit`}><Pencil className="w-4 h-4 mr-1.5" />Edit</Link>
            </Button>
          ) : null
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <KpiCard label="Audience" value={s.total ?? 0} icon={Send} testId="bc-total" />
        <KpiCard label="Sent" value={s.sent ?? 0} icon={Send} testId="bc-sent" />
        <KpiCard label="Delivered" value={s.delivered ?? 0} icon={CheckCheck} testId="bc-delivered" />
        <KpiCard label="Read" value={s.read ?? 0} icon={Eye} testId="bc-read" />
        <KpiCard label="Failed" value={s.failed ?? 0} icon={AlertCircle} testId="bc-failed" />
      </div>

      <Card className="mt-6 border-slate-200 shadow-none">
        <CardContent className="p-6">
          <div className="label-caps mb-3">Message</div>
          <div className="rounded-md bg-emerald-50 border border-emerald-100 p-3 text-sm text-slate-800 whitespace-pre-wrap">
            {b.message_body}
          </div>
        </CardContent>
      </Card>

      <Card className="mt-4 border-slate-200 shadow-none">
        <CardContent className="p-0">
          <div className="px-6 py-4 border-b border-slate-200">
            <div className="label-caps">Delivery log</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="p-3 text-left text-xs font-semibold tracking-wider uppercase text-slate-500">Phone</th>
                  <th className="p-3 text-left text-xs font-semibold tracking-wider uppercase text-slate-500">Status</th>
                  <th className="p-3 text-left text-xs font-semibold tracking-wider uppercase text-slate-500">Sent at</th>
                  <th className="p-3 text-left text-xs font-semibold tracking-wider uppercase text-slate-500">Delivered at</th>
                  <th className="p-3 text-left text-xs font-semibold tracking-wider uppercase text-slate-500">Error</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr><td colSpan={5} className="p-8 text-center text-slate-400 text-sm">No log entries yet</td></tr>
                ) : logs.map((l) => {
                  const badge = l.status === "failed"
                    ? "bg-red-50 text-red-700 border-red-200"
                    : l.status === "delivered" || l.status === "read"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-slate-50 text-slate-700 border-slate-200";
                  return (
                    <tr key={l.id} className="border-b border-slate-100" data-testid={`broadcast-log-row-${l.id}`}>
                      <td className="p-3 font-mono text-xs">{l.phone}</td>
                      <td className="p-3"><span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${badge}`}>{l.status}</span></td>
                      <td className="p-3 text-xs text-slate-500">{l.sent_at ? new Date(l.sent_at).toLocaleString() : "—"}</td>
                      <td className="p-3 text-xs text-slate-500">{l.delivered_at ? new Date(l.delivered_at).toLocaleString() : "—"}</td>
                      <td className="p-3 text-xs text-red-700 font-mono max-w-xs truncate" title={l.error || ""} data-testid={`broadcast-log-error-${l.id}`}>
                        {l.error || "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
